import { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardBody,
  Heading,
  Text,
  HStack,
  VStack,
  Button,
  ButtonGroup,
  Icon,
  Badge,
  Stat,
  StatLabel,
  StatNumber,
  StatHelpText,
  StatArrow,
  SimpleGrid,
  Center,
  Spinner,
  Divider,
  Tabs,
  TabList,
  Tab,
  TabPanels,
  TabPanel,
} from '@chakra-ui/react';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  AreaChart,
  Area,
  BarChart,
  Bar,
  Cell,
} from 'recharts';
import { 
  FaPlay, 
  FaSpotify, 
  FaMusic, 
  FaChartLine,
  FaCalendarDay,
  FaEye,
  FaArrowUp,
  FaDollarSign,
  FaMoneyBillWave,
  FaWallet
} from 'react-icons/fa';
import { SiShazam } from 'react-icons/si';
import axiosInstance from '../../services/axios';

const DailyStreamChart = ({ trackInfo }) => {
  const [streamData, setStreamData] = useState(null);
  const [earningsData, setEarningsData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [selectedPlatform, setSelectedPlatform] = useState('spotify');
  const [chartType, setChartType] = useState('area'); // area, line, bar
  const [timeRange, setTimeRange] = useState('14d'); // 7d, 14d, 30d
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState(0); // 0 = streams, 1 = earnings

  // Theme colors
  const cardBg = 'dark.100';
  const borderColor = 'dark.900';
  const textColor = 'primary.500';
  const mutedTextColor = 'secondary.900';
  const headingColor = 'primary.100';

  const platformConfig = {
    spotify: {
      name: 'Spotify',
      icon: FaSpotify,
      color: '#1ED760',
      gradientStart: '#1ED760',
      gradientEnd: '#1DB954',
      dataKey: 'daily',
      label: 'Daily Streams',
      description: 'Number of times the track was played',
      endpoint: 'stream_count'
    },
    shazam: {
      name: 'Shazam',
      icon: SiShazam,
      color: '#00A6FF',
      gradientStart: '#00A6FF',
      gradientEnd: '#0088FF',
      dataKey: 'daily',
      label: 'Daily Discoveries',
      description: 'New people discovering this song',
      endpoint: 'shazam'
    }
  };

  useEffect(() => {
    if (trackInfo?.isrc) {
      fetchStreamData();
    }
  }, [trackInfo, selectedPlatform, timeRange]);

  const fetchStreamData = async () => {
    if (!trackInfo?.isrc) return;
    
    setLoading(true);
    setError(null);
    
    try {
      let response;
      let earningsResponse;
      
      if (selectedPlatform === 'spotify') {
        // Fetch both stream data and earnings data
        response = await axiosInstance.get(
          `/spotontrack/stream_count/${trackInfo.isrc}?platform=spotify`
        );
        
        // Fetch earnings data specifically
        try {
          earningsResponse = await axiosInstance.get(
            `/spotontrack/stream_count/${trackInfo.isrc}/earnings?platform=spotify`
          );
        } catch (earningsError) {
          console.warn('Earnings data not available:', earningsError);
        }
      } else if (selectedPlatform === 'shazam') {
        response = await axiosInstance.get(
          `/spotontrack/shazam/${trackInfo.isrc}?platform=shazam`
        );
      }
      
      if (response?.data && Array.isArray(response.data)) {
        const processedData = processStreamData(response.data);
        setStreamData(processedData);
        
        // Set earnings data if available
        if (earningsResponse?.data?.earnings) {
          setEarningsData(earningsResponse.data.earnings);
        } else {
          setEarningsData(null);
        }
      } else {
        setError('No data available');
      }
    } catch (error) {
      console.error('Error fetching stream data:', error);
      setError('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const processStreamData = (data) => {
    if (!data || data.length === 0) return null;

    // Sort by date (newest first) and reverse to get chronological order for charts
    const sortedData = [...data].sort((a, b) => new Date(a.date) - new Date(b.date));
    
    // Apply time range filter
    let filteredData = sortedData;
    if (timeRange !== 'all') {
      const days = parseInt(timeRange.replace('d', ''));
      filteredData = sortedData.slice(-days);
    }

    // Calculate statistics
    const latest = sortedData[sortedData.length - 1];
    const previous = sortedData[sortedData.length - 2];
    const weekAgo = sortedData[Math.max(0, sortedData.length - 8)];
    
    const dailyChange = previous ? (latest.daily ?? 0) - (previous.daily ?? 0) : 0;
    const dailyChangePercent = previous && previous.daily > 0 ? ((dailyChange / previous.daily) * 100) : 0;
    
    const weeklyChange = weekAgo ? latest.total - weekAgo.total : 0;
    const averageDaily = filteredData.reduce((sum, item) => sum + (item.daily ?? 0), 0) / filteredData.length;
    
    const maxDaily = Math.max(...filteredData.map(item => item.daily ?? 0));
    const minDaily = Math.min(...filteredData.map(item => item.daily ?? 0));

    // Format data for charts - handle null daily values
    const chartData = filteredData.map(item => ({
      ...item,
      daily: item.daily ?? 0, // Replace null with 0
      date: new Date(item.date).toLocaleDateString('en-US', { 
        month: 'short', 
        day: 'numeric' 
      }),
      fullDate: item.date,
      totalFormatted: item.total.toLocaleString(),
      dailyFormatted: (item.daily ?? 0).toLocaleString()
    }));

    return {
      chartData,
      statistics: {
        latest: {
          total: latest.total,
          daily: latest.daily ?? 0,
          date: latest.date
        },
        changes: {
          daily: dailyChange,
          dailyPercent: dailyChangePercent,
          weekly: weeklyChange
        },
        metrics: {
          averageDaily: Math.round(averageDaily),
          maxDaily,
          minDaily,
          totalDays: filteredData.length
        }
      }
    };
  };

  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      const data = payload[0].payload;
      const config = platformConfig[selectedPlatform];
      return (
        <Card bg="dark.900" border="1px solid" borderColor="dark.900" p={3} boxShadow="lg">
          <VStack spacing={1} align="start">
            <Text color="white" fontWeight="bold" fontSize="sm">{data.fullDate}</Text>
            <Text color="dark.500" fontSize="sm">
              {selectedPlatform === 'spotify' ? 'Daily Streams' : 'Daily Discoveries'}: {data.dailyFormatted}
            </Text>
            <Text color="secondary.900" fontSize="sm">
              Total: {data.totalFormatted}
            </Text>
          </VStack>
        </Card>
      );
    }
    return null;
  };

  const renderChart = () => {
    // Show earnings chart if on earnings tab and have earnings data
    if (activeTab === 1 && earningsData && selectedPlatform === 'spotify') {
      return renderEarningsChart();
    }
    
    // Default to streams chart
    if (!streamData) return null;

    const config = platformConfig[selectedPlatform];
    const { chartData } = streamData;

    const commonProps = {
      data: chartData,
      margin: { top: 5, right: 30, left: 20, bottom: 5 }
    };

    switch (chartType) {
      case 'area':
        return (
          <ResponsiveContainer width="100%" height="100%">
            <AreaChart {...commonProps}>
              <defs>
                <linearGradient id={`gradient-${selectedPlatform}`} x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor={config.gradientStart} stopOpacity={0.3}/>
                  <stop offset="95%" stopColor={config.gradientEnd} stopOpacity={0.1}/>
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="#4A5568" />
              <XAxis 
                dataKey="date" 
                stroke="#9CA3AF" 
                fontSize={12}
                tick={{ fill: '#9CA3AF' }}
              />
              <YAxis 
                stroke="#9CA3AF" 
                fontSize={12}
                tick={{ fill: '#9CA3AF' }}
                tickFormatter={(value) => value.toLocaleString()}
              />
              <Tooltip content={<CustomTooltip />} />
              <Area
                type="monotone"
                dataKey="daily"
                stroke={config.color}
                strokeWidth={2}
                fill={`url(#gradient-${selectedPlatform})`}
              />
            </AreaChart>
          </ResponsiveContainer>
        );
      
      case 'line':
        return (
          <ResponsiveContainer width="100%" height="100%">
            <LineChart {...commonProps}>
              <CartesianGrid strokeDasharray="3 3" stroke="#4A5568" />
              <XAxis 
                dataKey="date" 
                stroke="#9CA3AF" 
                fontSize={12}
                tick={{ fill: '#9CA3AF' }}
              />
              <YAxis 
                stroke="#9CA3AF" 
                fontSize={12}
                tick={{ fill: '#9CA3AF' }}
                tickFormatter={(value) => value.toLocaleString()}
              />
              <Tooltip content={<CustomTooltip />} />
              <Line
                type="monotone"
                dataKey="daily"
                stroke={config.color}
                strokeWidth={3}
                dot={{ fill: config.color, strokeWidth: 2, r: 4 }}
                activeDot={{ r: 6, stroke: config.color, strokeWidth: 2 }}
              />
            </LineChart>
          </ResponsiveContainer>
        );
      
      case 'bar':
        return (
          <ResponsiveContainer width="100%" height="100%">
            <BarChart {...commonProps}>
              <CartesianGrid strokeDasharray="3 3" stroke="#4A5568" />
              <XAxis 
                dataKey="date" 
                stroke="#9CA3AF" 
                fontSize={12}
                tick={{ fill: '#9CA3AF' }}
              />
              <YAxis 
                stroke="#9CA3AF" 
                fontSize={12}
                tick={{ fill: '#9CA3AF' }}
                tickFormatter={(value) => value.toLocaleString()}
              />
              <Tooltip content={<CustomTooltip />} />
              <Bar dataKey="daily" radius={[2, 2, 0, 0]}>
                {chartData.map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={config.color} />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        );
      
      default:
        return null;
    }
  };

  const renderEarningsChart = () => {
    if (!earningsData?.detailed?.daily_earnings) return null;

    // Get last 30 days of earnings data filtered by time range
    const earningsChartData = earningsData.detailed.daily_earnings
      .slice(-parseInt(timeRange.replace('d', '')))
      .map(item => ({
        ...item,
        date: new Date(item.date).toLocaleDateString('en-US', { 
          month: 'short', 
          day: 'numeric' 
        }),
        fullDate: item.date,
        earningsFormatted: item.formatted_earnings
      }));

    const commonProps = {
      data: earningsChartData,
      margin: { top: 5, right: 30, left: 20, bottom: 5 }
    };

    const EarningsTooltip = ({ active, payload, label }) => {
      if (active && payload && payload.length) {
        const data = payload[0].payload;
        return (
          <Card bg="dark.900" border="1px solid" borderColor="dark.900" p={3} boxShadow="lg">
            <VStack spacing={1} align="start">
              <Text color="white" fontWeight="bold" fontSize="sm">{data.fullDate || 'N/A'}</Text>
              <Text color="green.400" fontSize="sm">
                Earnings: {data.earningsFormatted || '$0.00'}
              </Text>
              <Text color="secondary.900" fontSize="sm">
                Streams: {(data.streams || 0).toLocaleString()}
              </Text>
            </VStack>
          </Card>
        );
      }
      return null;
    };

    switch (chartType) {
      case 'area':
        return (
          <ResponsiveContainer width="100%" height="100%">
            <AreaChart {...commonProps}>
              <defs>
                <linearGradient id="earnings-gradient" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#48BB78" stopOpacity={0.3}/>
                  <stop offset="95%" stopColor="#38A169" stopOpacity={0.1}/>
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="#4A5568" />
              <XAxis 
                dataKey="date" 
                stroke="#9CA3AF" 
                fontSize={12}
                tick={{ fill: '#9CA3AF' }}
              />
              <YAxis 
                stroke="#9CA3AF" 
                fontSize={12}
                tick={{ fill: '#9CA3AF' }}
                tickFormatter={(value) => `$${value.toFixed(2)}`}
              />
              <Tooltip content={<EarningsTooltip />} />
              <Area
                type="monotone"
                dataKey="earnings"
                stroke="#48BB78"
                strokeWidth={2}
                fill="url(#earnings-gradient)"
              />
            </AreaChart>
          </ResponsiveContainer>
        );
      
      case 'line':
        return (
          <ResponsiveContainer width="100%" height="100%">
            <LineChart {...commonProps}>
              <CartesianGrid strokeDasharray="3 3" stroke="#4A5568" />
              <XAxis 
                dataKey="date" 
                stroke="#9CA3AF" 
                fontSize={12}
                tick={{ fill: '#9CA3AF' }}
              />
              <YAxis 
                stroke="#9CA3AF" 
                fontSize={12}
                tick={{ fill: '#9CA3AF' }}
                tickFormatter={(value) => `$${value.toFixed(2)}`}
              />
              <Tooltip content={<EarningsTooltip />} />
              <Line
                type="monotone"
                dataKey="earnings"
                stroke="#48BB78"
                strokeWidth={3}
                dot={{ fill: '#48BB78', strokeWidth: 2, r: 4 }}
                activeDot={{ r: 6, stroke: '#48BB78', strokeWidth: 2 }}
              />
            </LineChart>
          </ResponsiveContainer>
        );
      
      case 'bar':
        return (
          <ResponsiveContainer width="100%" height="100%">
            <BarChart {...commonProps}>
              <CartesianGrid strokeDasharray="3 3" stroke="#4A5568" />
              <XAxis 
                dataKey="date" 
                stroke="#9CA3AF" 
                fontSize={12}
                tick={{ fill: '#9CA3AF' }}
              />
              <YAxis 
                stroke="#9CA3AF" 
                fontSize={12}
                tick={{ fill: '#9CA3AF' }}
                tickFormatter={(value) => `$${value.toFixed(2)}`}
              />
              <Tooltip content={<EarningsTooltip />} />
              <Bar dataKey="earnings" radius={[2, 2, 0, 0]}>
                {earningsChartData.map((entry, index) => (
                  <Cell key={`cell-${index}`} fill="#48BB78" />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        );
      
      default:
        return null;
    }
  };

  const renderStreamingStats = () => {
    if (!streamData) return null;

    const { statistics } = streamData;
    const config = platformConfig[selectedPlatform];
    const isShazam = selectedPlatform === 'shazam';

    return (
      <SimpleGrid columns={{ base: 2, md: 4 }} spacing={4} mb={6}>
        {/* Latest Daily */}
        <Card bg="dark.500" border="1px solid" borderColor="dark.900">
          <CardBody p={4}>
            <Stat>
              <StatLabel color="secondary.900" fontSize="xs">
                {isShazam ? 'DAILY DISCOVERIES' : 'DAILY STREAMS'}
              </StatLabel>
              <StatNumber color={textColor} fontSize="xl">
                {statistics.latest.daily.toLocaleString()}
              </StatNumber>
              <StatHelpText color={statistics.changes.dailyPercent >= 0 ? 'green.400' : 'red.400'}>
                <StatArrow type={statistics.changes.dailyPercent >= 0 ? 'increase' : 'decrease'} />
                {Math.abs(statistics.changes.dailyPercent).toFixed(1)}%
              </StatHelpText>
            </Stat>
          </CardBody>
        </Card>

        {/* Total */}
        <Card bg="dark.500" border="1px solid" borderColor="dark.900">
          <CardBody p={2}>
            <Stat>
              <StatLabel color="secondary.900" fontSize="xs">
                {isShazam ? 'TOTAL DISCOVERIES' : 'TOTAL STREAMS'}
              </StatLabel>
              <StatNumber color={config.color} fontSize="xl">
                {statistics.latest.total > 1000000 
                  ? `${(statistics.latest.total / 1000000).toFixed(1)}M`
                  : statistics.latest.total > 1000
                  ? `${(statistics.latest.total / 1000).toFixed(1)}K`
                  : statistics.latest.total.toLocaleString()
                }
              </StatNumber>
              <StatHelpText color="secondary.900">
                {statistics.latest.total.toLocaleString()}
              </StatHelpText>
            </Stat>
          </CardBody>
        </Card>

        {/* Average Daily */}
        <Card bg="dark.500" border="1px solid" borderColor="dark.900">
          <CardBody p={2}>
            <Stat>
              <StatLabel color="secondary.900" fontSize="xs">AVG DAILY</StatLabel>
              <StatNumber color={textColor} fontSize="xl">
                {statistics.metrics.averageDaily.toLocaleString()}
              </StatNumber>
              <StatHelpText color="secondary.900">
                {timeRange} average
              </StatHelpText>
            </Stat>
          </CardBody>
        </Card>

        {/* Peak Daily */}
        <Card bg="dark.500" border="1px solid" borderColor="dark.900">
          <CardBody p={2}>
            <Stat>
              <StatLabel color="secondary.900" fontSize="xs">PEAK DAILY</StatLabel>
              <StatNumber color={textColor} fontSize="xl">
                {statistics.metrics.maxDaily.toLocaleString()}
              </StatNumber>
              <StatHelpText color="secondary.900">
                in {timeRange}
              </StatHelpText>
            </Stat>
          </CardBody>
        </Card>
      </SimpleGrid>
    );
  };

  const renderEarningsStats = () => {
    if (!earningsData || selectedPlatform !== 'spotify') return null;

    const { summary } = earningsData;

    return (
      <SimpleGrid columns={{ base: 2, md: 4 }} spacing={4} mb={6}>
        {/* Current Month Earnings */}
        <Card bg="dark.500" border="1px solid" borderColor="dark.900">
          <CardBody p={2}>
            <Stat>
              <StatLabel color="secondary.900" fontSize="xs">
                CURRENT MONTH
              </StatLabel>
              <StatNumber color="green.400" fontSize="xl">
                {summary.formatted.current_month}
              </StatNumber>
              <StatHelpText color="secondary.900">
                {new Date().toLocaleDateString('en-US', { month: 'short', year: 'numeric' })}
              </StatHelpText>
            </Stat>
          </CardBody>
        </Card>

        {/* Projected Month Earnings */}
        <Card bg="dark.500" border="1px solid" borderColor="dark.900">
          <CardBody p={2}>
            <Stat>
              <StatLabel color="secondary.900" fontSize="xs">
                PROJECTED MONTH
              </StatLabel>
              <StatNumber color="blue.400" fontSize="xl">
                {summary.formatted.projected_month}
              </StatNumber>
              <StatHelpText color="secondary.900">
                based on trends
              </StatHelpText>
            </Stat>
          </CardBody>
        </Card>

        {/* Last 30 Days */}
        <Card bg="dark.500" border="1px solid" borderColor="dark.900">
          <CardBody p={2}>
            <Stat>
              <StatLabel color="secondary.900" fontSize="xs">LAST 30 DAYS</StatLabel>
              <StatNumber color="yellow.400" fontSize="xl">
                {summary.formatted.last_30_days}
              </StatNumber>
              <StatHelpText color="secondary.900">
                rolling 30 days
              </StatHelpText>
            </Stat>
          </CardBody>
        </Card>

        {/* Stream Rate */}
        <Card bg="dark.500" border="1px solid" borderColor="dark.900">
          <CardBody p={2}>
            <Stat>
              <StatLabel color="secondary.900" fontSize="xs">RATE PER STREAM</StatLabel>
              <StatNumber color={textColor} fontSize="xl">
                {summary.formatted.stream_rate}
              </StatNumber>
              <StatHelpText color="secondary.900">
                USD per play
              </StatHelpText>
            </Stat>
          </CardBody>
        </Card>
      </SimpleGrid>
    );
  };

  if (!trackInfo?.isrc) {
    return (
      <Card bg={cardBg} border="1px solid" borderColor={borderColor}>
        <CardBody p={6}>
          <Center height="200px">
            <VStack spacing={4}>
              <Icon as={FaChartLine} boxSize={12} color="primary.100" />
              <Text color="secondary.900">No ISRC available for stream tracking</Text>
            </VStack>
          </Center>
        </CardBody>
      </Card>
    );
  }

  return (
    <Card bg={cardBg} border="1px solid" borderColor={borderColor} borderRadius="xl" overflow="hidden">
      <CardBody p={6}>
        {/* Header */}
        <VStack spacing={2} mb={6}>
          <HStack justify="space-between" width="100%" flexWrap="wrap" gap={4}>
            <HStack spacing={3}>
              <Icon as={FaChartLine} boxSize={6} color="secondary.900" />
              <VStack align="start" spacing={0}>
                <Heading size="lg" color={headingColor}>
                  Daily Analytics
                </Heading>
                <Text color={mutedTextColor} fontSize="sm">
                  {platformConfig[selectedPlatform].description}
                </Text>
              </VStack>
            </HStack>

            {/* Platform Selector */}
            <ButtonGroup variant="outline" size="sm" spacing={1}>
              {Object.entries(platformConfig).map(([key, config]) => (
                <Button
                  key={key}
                  onClick={() => setSelectedPlatform(key)}
                  bg={selectedPlatform === key ? config.color : 'transparent'}
                  color={selectedPlatform === key ? 'white' : config.color}
                  borderColor={config.color}
                  _hover={{ bg: config.color, color: 'white' }}
                  leftIcon={<Icon as={config.icon} />}
                  size="sm"
                >
                  {config.name}
                </Button>
              ))}
            </ButtonGroup>
          </HStack>

          {/* Controls */}
          <HStack justify="space-between" width="100%" flexWrap="wrap" gap={4}>
            {/* Time Range */}
            <ButtonGroup variant="outline" size="sm" spacing={1}>
              {[
                { key: '7d', label: '7D' },
                { key: '14d', label: '14D' },
                { key: '30d', label: '30D' }
              ].map(({ key, label }) => (
                <Button
                  key={key}
                  onClick={() => setTimeRange(key)}
                  bg={timeRange === key ? 'secondary.100' : 'transparent'}
                  color={timeRange === key ? 'dark.900' : 'secondary.900'}
                  borderColor="secondary.500"
                  _hover={{ bg: 'secondary.500', color: 'dark.900' }}
                >
                  {label}
                </Button>
              ))}
            </ButtonGroup>

            {/* Chart Type */}
            <ButtonGroup variant="outline" size="sm" spacing={1}>
              {[
                { key: 'area', label: 'Area', icon: FaArrowUp },
                { key: 'line', label: 'Line', icon: FaChartLine },
                { key: 'bar', label: 'Bar', icon: FaCalendarDay }
              ].map(({ key, label, icon }) => (
                <Button
                  key={key}
                  onClick={() => setChartType(key)}
                  bg={chartType === key ? 'secondary.900' : 'transparent'}
                  color={chartType === key ? 'dark.900' : 'secondary.900'}
                  borderColor="secondary.500"
                  _hover={{ bg: 'secondary.500', color: 'dark.900' }}
                  leftIcon={<Icon as={icon} />}
                >
                  {label}
                </Button>
              ))}
            </ButtonGroup>
          </HStack>
        </VStack>

          {selectedPlatform === 'spotify' && earningsData ? (
            <Tabs index={activeTab} onChange={setActiveTab} variant="line" colorScheme="secondary" mb={6}>
              <TabList>
                <Tab leftIcon={<Icon as={FaPlay} />} fontSize="sm" color="white">Streams</Tab>
                <Tab leftIcon={<Icon as={FaDollarSign} />} fontSize="sm" color="white">Earnings Estimate</Tab>
              </TabList>
              <TabPanels>
                <TabPanel px={0}>
            {renderStreamingStats()}
                </TabPanel>
                <TabPanel px={0}>
            {renderEarningsStats()}
                </TabPanel>
              </TabPanels>
            </Tabs>
          ) : (
            renderStreamingStats()
          )}

          {/* Chart */}
        <Box height="400px" position="relative">
          {loading ? (
            <Center height="100%">
              <VStack spacing={4}>
                <Spinner size="xl" color={platformConfig[selectedPlatform].color} thickness="4px" />
                <Text color={mutedTextColor}>Loading {selectedPlatform === 'shazam' ? 'discovery' : 'stream'} data...</Text>
              </VStack>
            </Center>
          ) : error ? (
            <Center height="100%">
              <VStack spacing={4}>
                <Icon as={selectedPlatform === 'shazam' ? FaEye : FaPlay} boxSize={16} color="primary.100" />
                <Heading size="md" color="primary.100">
                  {error}
                </Heading>
                <Text color={mutedTextColor} textAlign="center" maxW="400px">
                  {selectedPlatform === 'shazam' 
                    ? 'Discovery data might not be available for this track on Shazam.'
                    : 'Stream data might not be available for this track on Spotify.'
                  }
                </Text>
                <Button
                  size="sm"
                  variant="outline"
                  colorScheme="secondary"
                  onClick={fetchStreamData}
                >
                  Try Again
                </Button>
              </VStack>
            </Center>
          ) : (
            renderChart()
          )}
        </Box>

        {/* Footer Info */}
        {streamData && (
          <HStack justify="space-between" mt={4} flexWrap="wrap">
            <Text fontSize="sm" color={mutedTextColor}>
              Last updated: {new Date(streamData.statistics.latest.date).toLocaleDateString()}
            </Text>
            <Badge 
              colorScheme={selectedPlatform === 'spotify' ? 'green' : 'blue'} 
              variant="subtle"
            >
              {streamData.statistics.metrics.totalDays} days of data
            </Badge>
          </HStack>
        )}
      </CardBody>
    </Card>
  );
};

export default DailyStreamChart;
